import { generateVideoUploadUrlFn } from "@/actions/workout/server";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type UploadSuccessInfo,
  useUploadStore,
} from "@/lib/upload/upload-store";
import { createId } from "@paralleldrive/cuid2";
import { Loader2, Pause, Play, Scissors, Upload } from "lucide-react";
import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";

interface VideoTrimmerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (info: UploadSuccessInfo) => void;
  rowId?: string;
  exerciseId?: string;
  exerciseOrder?: number;
  setNumber?: number;
}

export function VideoTrimmerDialog({
  open,
  onOpenChange,
  onSuccess,
  rowId,
  exerciseId,
  exerciseOrder,
  setNumber,
}: VideoTrimmerDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);
  const [isPreparing, setIsPreparing] = useState(false); // presigned URL alınıyor
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState([0, 0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [isGeneratingThumbnails, setIsGeneratingThumbnails] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const rangeRef = useRef(range);
  const durationRef = useRef(duration);
  const wasPlayingRef = useRef(false);
  const isDraggingRef = useRef(false);

  const startUpload = useUploadStore((s) => s.startUpload);

  useEffect(() => {
    rangeRef.current = range;
  }, [range]);
  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  const resetState = useCallback(() => {
    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setThumbnails((prev) => {
      for (const url of prev) URL.revokeObjectURL(url);
      return [];
    });
    setFile(null);
    setVideoPreviewUrl(null);
    setDuration(0);
    setRange([0, 0]);
    setCurrentTime(0);
    setIsPlaying(false);
    setIsPreparing(false);
    setIsGeneratingThumbnails(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPreviewUrl]);

  useEffect(() => {
    if (!open) resetState();
  }, [open, resetState]);

  const generateThumbnails = useCallback(
    async (url: string, videoDuration: number) => {
      // Artık bloklamıyoruz, sadece arka planda çalışsın
      setIsGeneratingThumbnails(true);
      try {
        const vid = document.createElement("video");
        vid.id = "temp-thumb-video";
        vid.src = url;
        vid.muted = true;
        vid.playsInline = true;
        vid.setAttribute("webkit-playsinline", "true");
        vid.preload = "auto";
        vid.style.position = "fixed";
        vid.style.pointerEvents = "none";
        vid.style.opacity = "0";
        document.body.appendChild(vid);
        vid.load();

        // Wait for metadata or timeout
        await Promise.race([
          new Promise<void>((res) => {
            vid.onloadedmetadata = () => res();
          }),
          new Promise((_, rej) =>
            setTimeout(() => rej(new Error("Metadata timeout")), 5000),
          ),
        ]);

        const canvas = document.createElement("canvas");
        canvas.width = 160;
        canvas.height =
          Math.round((160 * vid.videoHeight) / vid.videoWidth) || 90;
        const ctx = canvas.getContext("2d", { alpha: false })!;
        const count = 8;
        const thumbUrls: string[] = [];

        for (let i = 0; i < count; i++) {
          try {
            const t = (i / count) * videoDuration;
            vid.currentTime = t;

            await Promise.race([
              new Promise<void>((res) => {
                vid.onseeked = () => {
                  vid.onseeked = null; // stale callback'i temizle
                  res();
                };
              }),
              new Promise((_, rej) =>
                setTimeout(() => {
                  vid.onseeked = null; // timeout'ta da temizle — bir sonraki seek'i erken resolve etmesin
                  rej(new Error("Seek timeout"));
                }, 2000),
              ),
            ]);

            // Ekstra bekleme: onseeked tetiklendiğinde kare her zaman hazır olmayabilir (siyah kareyi önler)
            await new Promise((res) => setTimeout(res, 150));

            ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise<Blob>((res) =>
              canvas.toBlob((b) => res(b!), "image/jpeg", 0.6),
            );
            const thumbUrl = URL.createObjectURL(blob);
            thumbUrls.push(thumbUrl);

            // Her resim oluştuğunda state'i güncelle (progressive loading)
            setThumbnails([...thumbUrls]);
          } catch (seekError) {
            console.warn(`Thumbnail ${i} hatası:`, seekError);
          }
        }
      } catch (e) {
        console.error("Zaman tüneli oluşturma hatası:", e);
      } finally {
        setIsGeneratingThumbnails(false);
        // DOM temizliği
        const vid = document.getElementById("temp-thumb-video");
        if (vid) document.body.removeChild(vid);
      }
    },
    [],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.size > 200 * 1024 * 1024) {
      toast.error("Video boyutu 200MB'dan küçük olmalıdır.");
      return;
    }

    if (videoPreviewUrl) URL.revokeObjectURL(videoPreviewUrl);
    setThumbnails((prev) => {
      for (const url of prev) URL.revokeObjectURL(url);
      return [];
    });

    setFile(selectedFile);
    setDuration(0);
    setRange([0, 0]);
    setCurrentTime(0);
    setIsPlaying(false);
    setVideoPreviewUrl(URL.createObjectURL(selectedFile));
  };

  const onLoadedMetadata = () => {
    if (videoRef.current && videoPreviewUrl) {
      const d = videoRef.current.duration;
      setDuration(d);
      setRange([0, Math.min(d, 180)]);

      // iOS için video dekoderini "uyandır" ve autoplay başlat
      const vid = videoRef.current;
      vid.currentTime = 0;
      vid.muted = true;
      vid
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((e) => {
          // Autoplay blocked — kullanıcı manuel play yapacak.
          // vid.currentTime = 0.1 kaldırıldı: userın hemen play'e basması
          // durumunda seek + play race → AbortError'a yol açıyordu.
          console.warn("Autoplay engellendi:", e);
          setIsPlaying(false);
        });

      // Arka planda başlat — main video oynatmasını engellemez
      generateThumbnails(videoPreviewUrl, d);
    }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch(() => {
        // Playback may be blocked by browser policy
      });
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current || isDraggingRef.current) return;
    const ct = videoRef.current.currentTime;
    setCurrentTime(ct);
    const [start, end] = rangeRef.current;

    // Döngü mantığı - toleranslar artırıldı
    if (ct >= end + 0.1 || ct < start - 0.3) {
      videoRef.current.currentTime = start;
      if (isPlaying) {
        videoRef.current.play().catch(() => {
          // Playback may be interrupted or blocked by the browser
        });
      }
    }
  };

  const handleLeftMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDraggingRef.current = true;
    wasPlayingRef.current = isPlaying;
    if (videoRef.current) videoRef.current.pause();

    const startX = e.clientX;
    const containerWidth = e.currentTarget.parentElement!.clientWidth;
    const initialLeft = rangeRef.current[0];

    const onMouseMove = (ev: MouseEvent) => {
      const delta =
        ((ev.clientX - startX) / containerWidth) * durationRef.current;
      const val = Math.max(
        0,
        Math.min(rangeRef.current[1] - 0.5, initialLeft + delta),
      );
      setRange([val, rangeRef.current[1]]);
      if (videoRef.current) {
        videoRef.current.currentTime = val;
        setCurrentTime(val);
      }
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      if (videoRef.current) {
        const [start] = rangeRef.current;
        videoRef.current.currentTime = start;
        setCurrentTime(start);
        // Değişiklik sonrası her zaman oynat (cycle/loop sağlamak için)
        videoRef.current.play().catch(() => {
          // Playback may be interrupted or blocked by the browser
        });
        setIsPlaying(true);
      }
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleLeftTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Sayfanın geri gitmesini veya kaymasını önlemek için
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();

    isDraggingRef.current = true;
    wasPlayingRef.current = isPlaying;
    if (videoRef.current) videoRef.current.pause();

    const startX = e.touches[0].clientX;
    const containerWidth = e.currentTarget.parentElement!.clientWidth;
    const initialLeft = rangeRef.current[0];

    const onTouchMove = (ev: TouchEvent) => {
      const delta =
        ((ev.touches[0].clientX - startX) / containerWidth) *
        durationRef.current;
      const val = Math.max(
        0,
        Math.min(rangeRef.current[1] - 0.5, initialLeft + delta),
      );
      setRange([val, rangeRef.current[1]]);
      if (videoRef.current) {
        videoRef.current.currentTime = val;
        setCurrentTime(val);
      }
    };

    const onTouchEnd = () => {
      isDraggingRef.current = false;
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);

      if (videoRef.current) {
        const [start] = rangeRef.current;
        videoRef.current.currentTime = start;
        setCurrentTime(start);
        // Değişiklik sonrası her zaman oynat (cycle/loop sağlamak için)
        videoRef.current.play().catch(() => {
          // Playback may be interrupted or blocked by the browser
        });
        setIsPlaying(true);
      }
    };
    document.addEventListener("touchmove", onTouchMove);
    document.addEventListener("touchend", onTouchEnd);
  };

  const handleRightMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isDraggingRef.current = true;
    wasPlayingRef.current = isPlaying;
    if (videoRef.current) videoRef.current.pause();

    const startX = e.clientX;
    const containerWidth = e.currentTarget.parentElement!.clientWidth;
    const initialRight = rangeRef.current[1];

    const onMouseMove = (ev: MouseEvent) => {
      const delta =
        ((ev.clientX - startX) / containerWidth) * durationRef.current;
      const val = Math.max(
        rangeRef.current[0] + 0.5,
        Math.min(durationRef.current, initialRight + delta),
      );
      setRange([rangeRef.current[0], val]);
      if (videoRef.current) {
        videoRef.current.currentTime = val;
        setCurrentTime(val);
      }
    };

    const onMouseUp = () => {
      isDraggingRef.current = false;
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);

      if (videoRef.current) {
        const [start] = rangeRef.current;
        videoRef.current.currentTime = start;
        setCurrentTime(start);
        // Değişiklik sonrası her zaman oynat (cycle/loop sağlamak için)
        videoRef.current.play().catch(() => {
          // Playback may be interrupted or blocked by the browser
        });
        setIsPlaying(true);
      }
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const handleRightTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    // Sayfanın geri gitmesini veya kaymasını önlemek için
    if (e.cancelable) e.preventDefault();
    e.stopPropagation();

    isDraggingRef.current = true;
    wasPlayingRef.current = isPlaying;
    if (videoRef.current) videoRef.current.pause();

    const startX = e.touches[0].clientX;
    const containerWidth = e.currentTarget.parentElement!.clientWidth;
    const initialRight = rangeRef.current[1];

    const onTouchMove = (ev: TouchEvent) => {
      const delta =
        ((ev.touches[0].clientX - startX) / containerWidth) *
        durationRef.current;
      const val = Math.max(
        rangeRef.current[0] + 0.5,
        Math.min(durationRef.current, initialRight + delta),
      );
      setRange([rangeRef.current[0], val]);
      if (videoRef.current) {
        videoRef.current.currentTime = val;
        setCurrentTime(val);
      }
    };

    const onTouchEnd = () => {
      isDraggingRef.current = false;
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);

      if (videoRef.current) {
        const [start] = rangeRef.current;
        videoRef.current.currentTime = start;
        setCurrentTime(start);
        // Değişiklik sonrası her zaman oynat (cycle/loop sağlamak için)
        videoRef.current.play().catch(() => {
          // Playback may be interrupted or blocked by the browser
        });
        setIsPlaying(true);
      }
    };
    document.addEventListener("touchmove", onTouchMove);
    document.addEventListener("touchend", onTouchEnd);
  };

  const handleUpload = async () => {
    if (!file || durationRef.current === 0) return;

    try {
      setIsPreparing(true);

      // 1. Presigned URL al
      const ext = file.name.split(".").pop() ?? "mp4";
      const sigResult = await generateVideoUploadUrlFn({
        data: {
          fileName: `raw_${Date.now()}.${ext}`,
          fileSize: file.size,
          contentType: file.type || "video/mp4",
        },
      });

      if (
        sigResult.status !== 200 ||
        !sigResult.body ||
        "message" in sigResult.body
      ) {
        throw new Error("Yükleme adresi alınamadı");
      }

      const { url: uploadUrl, publicUrl } = sigResult.body as {
        url: string;
        key: string;
        publicUrl: string;
      };

      const [trimStart, trimEnd] = rangeRef.current;

      // 2. Store'a ekle ve XHR'ı başlat — dialog kapanır, upload devam eder
      startUpload({
        id: createId(),
        rowId,
        file,
        fileName: file.name,
        uploadUrl,
        publicUrl,
        trimStart,
        trimEnd,
        duration: durationRef.current,
        exerciseId,
        exerciseOrder,
        setNumber,
        onSuccess,
      });

      // Dialog hemen kapanır
      onOpenChange(false);
    } catch (error) {
      console.error("Upload hazırlık hatası:", error);
      setIsPreparing(false);
      toast.error(
        error instanceof Error ? `Hata: ${error.message}` : "Bir hata oluştu.",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[95vw] max-w-2xl flex-col overflow-hidden rounded-xl border bg-background p-0 sm:w-full">
        <DialogHeader className="shrink-0 border-b px-4 py-4 sm:px-6">
          <div className="flex flex-col gap-1 text-left sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate font-bold text-foreground text-lg sm:text-xl">
                Video Kırp ve Yükle
              </DialogTitle>
              <div className="mt-0.5 flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span className="font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                  {file ? "Videonuzu Düzenleyin" : "Dosya Seçimi"}
                </span>
              </div>
            </div>
            {file && (
              <div className="hidden sm:block">
                <div className="rounded-full bg-muted px-3 py-1 font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                  {(range[1] - range[0]).toFixed(1)}sn Seçildi
                </div>
              </div>
            )}
          </div>
        </DialogHeader>

        <div className="no-scrollbar flex-1 overflow-y-auto px-4 py-6 sm:px-6">
          {!file ? (
            <div className="group relative flex flex-col items-center justify-center rounded-xl border border-dashed bg-muted/5 py-12 text-center transition-all hover:bg-muted/10 sm:py-20">
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted/10">
                <Upload className="h-8 w-8 text-muted-foreground/30" />
              </div>
              <h3 className="mb-2 font-bold text-foreground text-lg">
                Videonuzu Buraya Bırakın
              </h3>
              <p className="mb-8 max-w-[240px] font-semibold text-[10px] text-muted-foreground uppercase tracking-wide">
                Maksimum 200MB • MP4 / MOV / WEBM
              </p>
              <button
                type="button"
                className="inline-flex h-12 items-center justify-center rounded-xl bg-primary px-10 font-bold text-primary-foreground transition-all hover:opacity-90 active:scale-95"
              >
                <label className="flex h-full w-full cursor-pointer items-center justify-center gap-2">
                  Dosya Seç
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              </button>
            </div>
          ) : (
            <div className="space-y-8 pb-4">
              {/* Video Preview */}
              <div className="relative aspect-video overflow-hidden rounded-xl border bg-black">
                <video
                  key={videoPreviewUrl!}
                  ref={videoRef}
                  src={videoPreviewUrl!}
                  className="h-full w-full object-contain"
                  onLoadedMetadata={onLoadedMetadata}
                  onTimeUpdate={handleTimeUpdate}
                  playsInline
                  autoPlay
                  muted
                  webkit-playsinline="true"
                />
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity ${
                    isPlaying ? "opacity-0 hover:opacity-100" : "opacity-100"
                  }`}
                >
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={togglePlay}
                    className="h-14 w-14 rounded-full border-none bg-background/20 text-white backdrop-blur-sm"
                  >
                    {isPlaying ? (
                      <Pause className="h-6 w-6 fill-current" />
                    ) : (
                      <Play className="ml-1 h-6 w-6 fill-current" />
                    )}
                  </Button>
                </div>
                <div className="absolute right-4 bottom-4 rounded border border-white/20 bg-black/40 px-2 py-0.5 font-mono text-[9px] text-white">
                  {currentTime.toFixed(1)}s / {duration.toFixed(1)}s
                </div>
              </div>

              <div className="space-y-6">
                {/* Time Indicators */}
                <div className="grid grid-cols-3 gap-2 px-1">
                  <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3">
                    <span className="font-semibold text-[9px] text-muted-foreground uppercase tracking-wide">
                      Başlangıç
                    </span>
                    <span className="font-bold text-foreground text-sm">
                      {range[0].toFixed(1)}s
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 rounded-lg border bg-muted/50 p-3">
                    <span className="text-center font-semibold text-[9px] text-muted-foreground uppercase tracking-wide">
                      Toplam Süre
                    </span>
                    <span className="text-center font-bold text-foreground text-sm">
                      {(range[1] - range[0]).toFixed(1)}s
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3 text-right">
                    <span className="font-semibold text-[9px] text-muted-foreground uppercase tracking-wide">
                      Bitiş
                    </span>
                    <span className="font-bold text-foreground text-sm">
                      {range[1].toFixed(1)}s
                    </span>
                  </div>
                </div>

                {/* Timeline */}
                <div className="group relative h-16 w-full touch-none select-none overflow-hidden border bg-black/5">
                  <div className="flex h-full w-full">
                    {thumbnails.length > 0 ? (
                      thumbnails.map((src, i) => (
                        <div key={i} className="h-full flex-1">
                          <img
                            src={src}
                            alt=""
                            className="h-full w-full object-cover opacity-60"
                          />
                        </div>
                      ))
                    ) : isGeneratingThumbnails ? (
                      <div className="flex w-full items-center justify-center gap-3 text-[9px] text-muted-foreground/30 uppercase tracking-wide">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Film Şeridi Hazırlanıyor...
                      </div>
                    ) : (
                      <div className="w-full bg-muted/50" />
                    )}
                  </div>

                  {/* Range Shadows */}
                  <div
                    className="absolute inset-y-0 left-0 bg-black/70 backdrop-blur-[2px] transition-colors group-hover:bg-black/60"
                    style={{ width: `${(range[0] / duration) * 100}%` }}
                  />
                  <div
                    className="absolute inset-y-0 right-0 bg-black/70 backdrop-blur-[2px] transition-colors group-hover:bg-black/60"
                    style={{ width: `${100 - (range[1] / duration) * 100}%` }}
                  />

                  {/* Active Highlight */}
                  <div
                    className="absolute inset-y-0 border-primary border-y-2"
                    style={{
                      left: `${(range[0] / duration) * 100}%`,
                      right: `${100 - (range[1] / duration) * 100}%`,
                    }}
                  />

                  {/* Current Time Indicator */}
                  <div
                    className="pointer-events-none absolute inset-y-0 z-30 w-1 bg-white shadow-[0_0_10px_rgba(255,255,255,1)]"
                    style={{ left: `${(currentTime / duration) * 100}%` }}
                  />

                  {/* Handles */}
                  <div
                    className="absolute inset-y-0 z-40 flex w-10 cursor-ew-resize items-center justify-center sm:w-8"
                    style={{
                      left: `${(range[0] / duration) * 100}%`,
                      transform: "translateX(-50%)",
                    }}
                    onMouseDown={handleLeftMouseDown}
                    onTouchStart={handleLeftTouchStart}
                  >
                    <div className="h-10 w-1.5 rounded-none bg-primary ring-2 ring-background transition-transform active:scale-x-150 sm:h-8" />
                  </div>
                  <div
                    className="absolute inset-y-0 z-40 flex w-10 cursor-ew-resize items-center justify-center sm:w-8"
                    style={{
                      left: `${(range[1] / duration) * 100}%`,
                      transform: "translateX(-50%)",
                    }}
                    onMouseDown={handleRightMouseDown}
                    onTouchStart={handleRightTouchStart}
                  >
                    <div className="h-10 w-1.5 rounded-none bg-primary ring-2 ring-background transition-transform active:scale-x-150 sm:h-8" />
                  </div>
                </div>

                <div className="flex items-start gap-4 rounded-xl border bg-muted/5 p-4">
                  <div className="mt-0.5 rounded-full bg-muted p-1.5">
                    <Scissors className="h-3 w-3 text-muted-foreground" />
                  </div>
                  <p className="font-medium text-[10px] text-muted-foreground leading-relaxed">
                    Tutacakları sürükleyerek videonun en iyi kısmını belirleyin.
                    Seçtiğiniz alan sunucu tarafından otomatik olarak
                    kırpılacaktır.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {file && (
          <DialogFooter className="shrink-0 border-t bg-background p-4 sm:flex-row sm:items-center sm:gap-3 sm:px-6">
            <Button
              variant="ghost"
              onClick={() => resetState()}
              disabled={isPreparing}
              className="h-10 w-full rounded-lg font-medium text-muted-foreground sm:w-auto"
            >
              Vazgeç
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isPreparing || duration === 0}
              className="h-10 flex-1 rounded-lg font-medium"
            >
              {isPreparing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Hazırlanıyor...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Videoyu Yayınla
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
